import React, { useEffect, useState } from "react";
import { Truck, Plus, X, PackageCheck, Trash2, ScanLine, Image as ImageIcon } from "lucide-react";
import { api, uploadImage, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { rupiah, num, fmtDate } from "@/lib/format";
import { Card, Loading, EmptyState, PageHeader, Button, Modal, Badge } from "@/components/common";
import BarcodeScanner from "@/components/BarcodeScanner";

const EXTRA_FIELDS = [["ongkir", "Ongkos kirim"], ["transport", "Transport"], ["tol", "Tol"], ["bongkar_muat", "Bongkar muat"], ["packing", "Box / Packing"], ["lainnya", "Biaya lain"]];

const STATUS = { pending: ["Menunggu Terima", "bg-amber-100 text-amber-700"], received: ["Diterima", "bg-emerald-100 text-emerald-700"], splitting: ["Dibagi", "bg-sky-100 text-sky-700"], completed: ["Selesai", "bg-slate-100 text-slate-600"] };

export default function Shipments() {
  const { branch, branches, can, isOwner } = useAuth();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const hub = branches.find((b) => b.type === "hub") || branch;

  const load = async () => {
    setLoading(true);
    try { setList(await api.get("/shipments")); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const del = async (s) => {
    if (!window.confirm(`Hapus permanen barang masuk ${s.shipment_no}? Stok yang sempat masuk akan dikembalikan. Tindakan ini tidak bisa dibatalkan.`)) return;
    try { await api.del(`/shipments/${s.id}`); toast.success("Barang masuk dihapus"); load(); }
    catch (e) { toast.error(e.message); }
  };

  const receive = async (s) => {
    if (!window.confirm(`Terima semua barang ${s.shipment_no} sesuai jumlah pesan (tanpa kerusakan)?`)) return;
    try {
      await api.post(`/shipments/${s.id}/receive`, { items: s.items.map((l) => ({ material_id: l.material_id, qty_ordered: l.qty_ordered, qty_received: l.qty_ordered, qty_damaged: 0, cost_price: l.cost_price })) });
      toast.success("Barang diterima ke stok hub"); load();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <>
      <PageHeader title="Barang Masuk (Kiriman)" subtitle="Penerimaan dari supplier ke Toko Utama" icon={Truck}
        actions={can("goods_receiving", "create") && <Button onClick={() => setCreating(true)} data-testid="add-shipment-btn"><Plus size={16} /> Kiriman Baru</Button>} />

      {loading ? <Loading /> : !list.length ? <EmptyState icon={Truck} title="Belum ada kiriman" /> : (
        <div className="space-y-2">
          {list.map((s) => {
            const [label, cls] = STATUS[s.status] || ["-", ""];
            return (
              <Card key={s.id} className="p-4" data-testid={`shipment-${s.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="font-medium text-slate-800">{s.shipment_no} · {s.supplier}</div>
                    <div className="text-xs text-slate-400">{s.items.length} item · {rupiah(s.total_cost)} · {s.payment_terms === "credit" ? `Tempo${s.due_date ? " " + fmtDate(s.due_date) : ""}` : "Tunai"}</div>
                    {s.total_extra > 0 && <div className="text-xs text-amber-600">Barang {rupiah(s.goods_cost)} + biaya trip {rupiah(s.total_extra)}</div>}
                    <div className="text-xs text-slate-500 mt-1">{s.items.map((l) => `${l.material_name} ×${num(l.qty_ordered)}`).join(", ")}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={cls}>{label}</Badge>
                    {s.status === "pending" && can("goods_receiving", "edit") && <Button size="sm" onClick={() => receive(s)}><PackageCheck size={15} /> Terima</Button>}
                    {isOwner && <button onClick={() => del(s)} className="w-8 h-8 rounded-lg hover:bg-rose-50 flex items-center justify-center text-rose-500" data-testid={`delete-shipment-${s.id}`} title="Hapus (Owner)"><Trash2 size={15} /></button>}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {creating && <ShipmentCreate hub={hub} onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
    </>
  );
}

function ShipmentCreate({ hub, onClose, onSaved }) {
  const [form, setForm] = useState({ supplier: "", payment_terms: "cash", due_date: "", trip_date: new Date().toISOString().slice(0, 10), origin: "", reference: "" });
  const [extra, setExtra] = useState({});
  const [notaPhoto, setNotaPhoto] = useState(null);
  const [materials, setMaterials] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [lines, setLines] = useState([{ material_id: "", qty_ordered: 1, cost_price: 0, best_before: "" }]);
  const [busy, setBusy] = useState(false);
  const [scan, setScan] = useState(false);
  const [uploading, setUploading] = useState(false);
  useEffect(() => {
    api.get("/materials").then(setMaterials);
    api.get("/suppliers").then((r) => setSuppliers(r.suppliers || [])).catch(() => {});
  }, []);

  const extraTotal = EXTRA_FIELDS.reduce((t, [k]) => t + (Number(extra[k]) || 0), 0);
  const goodsTotal = lines.reduce((t, l) => t + (Number(l.qty_ordered) || 0) * (Number(l.cost_price) || 0), 0);
  const matById = (id) => materials.find((m) => m.id === id);
  const defUnit = (m) => (m?.purchase_unit && m?.conversion_factor ? m.purchase_unit : m?.unit || "");

  const addBySku = (code) => {
    const n = String(code).trim().toLowerCase();
    const m = materials.find((x) => (x.sku || "").toLowerCase() === n);
    if (!m) { toast.error(`SKU "${code}" tidak ditemukan`); return; }
    if (lines.some((l) => l.material_id === m.id)) { toast.message(`${m.name} sudah ada`); return; }
    const u = defUnit(m);
    const c = u === m.purchase_unit ? (m.cost_price || 0) * (m.conversion_factor || 1) : (m.cost_price || 0);
    setLines((l) => [...l.filter((x) => x.material_id), { material_id: m.id, qty_ordered: 1, cost_price: c, unit: u, factor: m.conversion_factor || "", best_before: "" }]);
    toast.success(`${m.name} ditambahkan`);
  };

  const onNotaFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error("Ukuran foto maksimal 8MB"); return; }
    setUploading(true);
    try { const up = await uploadImage(file); setNotaPhoto(up.id); } catch { toast.error("Gagal unggah foto"); } finally { setUploading(false); }
  };

  const save = async () => {
    if (!hub) return toast.error("Toko Utama tidak ditemukan");
    if (!form.supplier.trim()) return toast.error("Supplier wajib diisi");
    const items = lines.filter((l) => l.material_id).map((l) => { const m = matById(l.material_id); const isB = m?.purchase_unit && l.unit === m.purchase_unit; return { material_id: l.material_id, qty_ordered: Number(l.qty_ordered), cost_price: Number(l.cost_price), cost_unit: l.unit || null, unit: l.unit || null, conversion_factor: isB && Number(l.factor) > 0 ? Number(l.factor) : null, best_before: l.best_before || null }; });
    if (!items.length) return toast.error("Tambah item");
    const extra_costs = {};
    EXTRA_FIELDS.forEach(([k]) => { if (Number(extra[k]) > 0) extra_costs[k] = Number(extra[k]); });
    setBusy(true);
    try {
      const body = { hub_branch_id: hub.id, supplier: form.supplier, origin: form.origin, reference: form.reference, payment_terms: form.payment_terms, items, trip_date: form.trip_date, extra_costs, nota_photo_file_id: notaPhoto };
      if (form.payment_terms === "credit" && form.due_date) body.due_date = form.due_date;
      await api.post("/shipments", body); toast.success("Kiriman dibuat"); onSaved();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <Modal open onClose={onClose} title="Barang Masuk (Trip Kedatangan)" maxWidth="max-w-2xl">
      <div className="space-y-3">
        <label className="block"><span className="text-sm font-medium text-slate-600 mb-1 block">Supplier</span>
          <input list="supplier-list" value={form.supplier} onChange={(e) => setForm({ ...form, supplier: e.target.value })} placeholder="Pilih / ketik baru" className="w-full h-10 px-3 rounded-xl border border-slate-300" data-testid="sf-supplier" />
          <datalist id="supplier-list">{suppliers.map((sp) => <option key={sp.name} value={sp.name} />)}</datalist>
        </label>
        <div className="grid grid-cols-3 gap-3">
          <label className="block"><span className="text-sm font-medium text-slate-600 mb-1 block">Tanggal datang</span><input type="date" value={form.trip_date} onChange={(e) => setForm({ ...form, trip_date: e.target.value })} className="w-full h-10 px-3 rounded-xl border border-slate-300" data-testid="sf-trip-date" /></label>
          <label className="block"><span className="text-sm font-medium text-slate-600 mb-1 block">Asal kota</span><input value={form.origin} onChange={(e) => setForm({ ...form, origin: e.target.value })} className="w-full h-10 px-3 rounded-xl border border-slate-300" /></label>
          <label className="block"><span className="text-sm font-medium text-slate-600 mb-1 block">No. resi (opsional)</span><input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} className="w-full h-10 px-3 rounded-xl border border-slate-300" /></label>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <label className="block"><span className="text-sm font-medium text-slate-600 mb-1 block">Pembayaran</span>
            <select value={form.payment_terms} onChange={(e) => setForm({ ...form, payment_terms: e.target.value })} className="w-full h-10 px-3 rounded-xl border border-slate-300"><option value="cash">Tunai</option><option value="credit">Tempo (Hutang)</option></select></label>
          {form.payment_terms === "credit" && <label className="block"><span className="text-sm font-medium text-slate-600 mb-1 block">Jatuh Tempo</span><input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="w-full h-10 px-3 rounded-xl border border-slate-300" /></label>}
        </div>

        <div className="rounded-xl border border-slate-200 p-3">
          <div className="text-sm font-medium text-slate-600 mb-2">Biaya Trip (dibagi proporsional → HPP per bahan)</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {EXTRA_FIELDS.map(([k, lbl]) => (
              <label key={k} className="block"><span className="text-xs text-slate-500">{lbl}</span>
                <input type="number" value={extra[k] ?? ""} onChange={(e) => setExtra((p) => ({ ...p, [k]: e.target.value }))} placeholder="0" className="w-full h-9 px-2 rounded-lg border border-slate-300" data-testid={`sf-extra-${k}`} /></label>
            ))}
          </div>
          <div className="text-xs text-slate-500 mt-2">Total biaya trip: <span className="font-semibold">{rupiah(extraTotal)}</span></div>
        </div>

        <div className="flex items-center gap-3">
          <input id="nota-file" type="file" accept="image/*" className="hidden" onChange={onNotaFile} data-testid="sf-nota-input" />
          <label htmlFor="nota-file" className="inline-flex items-center gap-1.5 h-9 px-3 rounded-xl border border-slate-300 text-sm cursor-pointer"><ImageIcon size={15} /> {uploading ? "Mengunggah..." : "Foto Nota / Surat Jalan"}</label>
          {notaPhoto && <img src={fileUrl(notaPhoto)} alt="nota" className="w-10 h-10 rounded-lg object-cover border" />}
        </div>

        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm font-medium text-slate-600">Item</span>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={() => setScan(true)} data-testid="sf-scan"><ScanLine size={14} /> Scan</Button>
              <Button size="sm" variant="subtle" onClick={() => setLines((l) => [...l, { material_id: "", qty_ordered: 1, cost_price: 0, best_before: "" }])}><Plus size={14} /></Button>
            </div>
          </div>
          {lines.map((l, i) => {
            const m = matById(l.material_id);
            const hasBundle = m?.purchase_unit && m?.conversion_factor;
            const isBundle = hasBundle && l.unit === m.purchase_unit;
            const fac = Number(l.factor) || m?.conversion_factor || 0;
            const hppPerBase = isBundle && Number(l.cost_price) && fac ? Number(l.cost_price) / fac : null;
            return (
              <div key={i} className="mb-2 rounded-xl border border-slate-100 p-2">
                <div className="flex flex-wrap gap-2 items-center">
                  <select value={l.material_id} onChange={(e) => { const mm = materials.find((x) => x.id === e.target.value); const u = defUnit(mm); const c = u === mm?.purchase_unit ? (mm?.cost_price || 0) * (mm?.conversion_factor || 1) : (mm?.cost_price || 0); setLines((ls) => ls.map((x, idx) => idx === i ? { ...x, material_id: e.target.value, unit: u, factor: mm?.conversion_factor || "", cost_price: x.cost_price || c } : x)); }} className="flex-1 min-w-[140px] h-10 px-2 rounded-xl border border-slate-300 text-sm" data-testid={`sf-line-mat-${i}`}><option value="">Bahan...</option>{materials.map((mm) => <option key={mm.id} value={mm.id}>{mm.name}</option>)}</select>
                  {hasBundle && (
                    <div className="flex rounded-lg overflow-hidden border border-slate-300 text-xs">
                      <button type="button" onClick={() => setLines((ls) => ls.map((x, idx) => idx === i ? { ...x, unit: m.purchase_unit } : x))} className={`px-2 h-10 ${isBundle ? "bg-primary text-white" : "bg-white text-slate-500"}`} data-testid={`sf-unit-bundle-${i}`}>{m.purchase_unit}</button>
                      <button type="button" onClick={() => setLines((ls) => ls.map((x, idx) => idx === i ? { ...x, unit: m.unit } : x))} className={`px-2 h-10 ${!isBundle ? "bg-primary text-white" : "bg-white text-slate-500"}`} data-testid={`sf-unit-base-${i}`}>{m.unit}</button>
                    </div>
                  )}
                  <input type="number" value={l.qty_ordered} onChange={(e) => setLines((ls) => ls.map((x, idx) => idx === i ? { ...x, qty_ordered: e.target.value } : x))} placeholder={`Qty ${l.unit || ""}`} className="w-20 h-10 px-2 rounded-xl border border-slate-300" data-testid={`sf-qty-${i}`} />
                  <input type="number" value={l.cost_price} onChange={(e) => setLines((ls) => ls.map((x, idx) => idx === i ? { ...x, cost_price: e.target.value } : x))} placeholder={`Rp/${l.unit || "unit"}`} className="w-28 h-10 px-2 rounded-xl border border-slate-300" data-testid={`sf-cost-${i}`} />
                  <input type="date" value={l.best_before} onChange={(e) => setLines((ls) => ls.map((x, idx) => idx === i ? { ...x, best_before: e.target.value } : x))} title="Best before (opsional)" className="w-36 h-10 px-2 rounded-xl border border-slate-300 text-sm" />
                  <button onClick={() => setLines((ls) => ls.filter((_, idx) => idx !== i))} className="w-8 text-rose-500"><X size={16} /></button>
                </div>
                {isBundle && (
                  <div className="flex items-center gap-2 mt-2 pl-1 flex-wrap">
                    <span className="text-xs text-slate-500">Isi 1 {m.purchase_unit} (trip ini):</span>
                    <input type="number" value={l.factor} onChange={(e) => setLines((ls) => ls.map((x, idx) => idx === i ? { ...x, factor: e.target.value } : x))} className="w-16 h-8 px-2 rounded-lg border border-slate-300 text-sm" data-testid={`sf-factor-${i}`} />
                    <span className="text-xs text-slate-500">{m.unit}</span>
                    <span className="text-xs text-emerald-600">{hppPerBase ? `HPP dasar ≈ ${rupiah(hppPerBase)}/${m.unit}` : ""}{Number(l.qty_ordered) > 0 && fac ? ` · ${num(Number(l.qty_ordered) * fac)} ${m.unit} masuk stok` : ""}</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div className="text-sm text-slate-600">Barang {rupiah(goodsTotal)} + biaya trip {rupiah(extraTotal)} = <span className="font-semibold text-slate-800">Total {rupiah(goodsTotal + extraTotal)}</span></div>
        <Button className="w-full" onClick={save} disabled={busy} data-testid="sf-save">{busy ? "Menyimpan..." : "Simpan Kiriman"}</Button>
      </div>
      <BarcodeScanner open={scan} onClose={() => setScan(false)} onScanned={addBySku} title="Scan SKU Bahan" />
    </Modal>
  );
}
